import { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { usePostOfficeContext } from '@atlassian/post-office-context';

import { useSubscriptionAdapter } from './adapter-provider';
import type {
	AdapterSubscriptionConfig,
	PlacementSubscriptionConfig,
	PlacementSubscriptionResult,
	SerializableRecord,
	SubscriptionAdapter,
	SubscriptionAdapterResult,
} from './types';

// https://switcheroo.atlassian.com/ui/gates/9ff1342e-2222-40e9-ba79-a3bca4ecfe82/key/post-office_nullified_subscription_variable_gate
export const NULLIFIED_SUBSCRIPTION_VARIABLE_GATE =
	'post-office_nullified_subscription_variable_gate';

/**
 * Stable no-op adapter that returns an error state.
 * Used to satisfy Rules of Hooks when the real adapter is not available.
 * Uses `never` type since it never successfully returns data.
 */
const noOpAdapter: SubscriptionAdapter<never> = {
	useSubscription: (_config: AdapterSubscriptionConfig): SubscriptionAdapterResult<never> => ({
		status: 'error',
		data: null,
		error: new Error(
			'No subscription adapter provided. Products must provide a subscription adapter via StableSubscriptionAdapterProvider.',
		),
		isLoading: false,
	}),
};

function adapterResultToPlacementResult<T extends SerializableRecord>(
	adapterResult: SubscriptionAdapterResult<T>,
	isEnabledForLiveMessages: boolean,
): PlacementSubscriptionResult<T> {
	return {
		data:
			adapterResult.data === null || adapterResult.data === undefined
				? null
				: (adapterResult.data as T),
		error: adapterResult.error,
		isLoading: adapterResult.isLoading,
		isEnabledForLiveMessages,
	};
}

/**
 * Hook for subscribing to real-time placement message updates via GraphQL subscriptions.
 * Automatically respects placement configuration - only subscribes if placement config has `subscription: { enabled: true }`.
 * Uses the subscription adapter provided via context (products provide their own adapter implementation).
 *
 * @param config - Subscription configuration
 * @returns Subscription result with data, error, loading state, and isEnabledForLiveMessages status
 *
 * @example
 * ```typescript
 * const { placementId } = usePlacementContext();
 * const { subscription: subscriptionConfig } = useSubscriptionConfig();
 * const subscriptionDocument = useSubscriptionDocument(placementId);
 *
 * const { data, error, isLoading, isEnabledForLiveMessages } = usePlacementSubscription({
 *   subscription: subscriptionDocument,
 *   placementId,
 *   subscriptionSettings: subscriptionConfig,
 *   variables: {},
 * });
 * ```
 */
export const usePlacementSubscription = <T extends SerializableRecord>(
	config: PlacementSubscriptionConfig,
): PlacementSubscriptionResult<T> => {
	const postOfficeContext = usePostOfficeContext();
	const { subscriptionAdapter } = useSubscriptionAdapter();

	const placementId = config.placementId;
	const subscriptionConfig = config.subscriptionSettings;

	const isEnabledForLiveMessages = subscriptionConfig?.subscription?.enabled ?? false;

	const isNullifiedSubscriptionVariableGateEnabled = fg(
		'post-office_nullified_subscription_variable_gate',
	);

	const subscriptionVariables = useMemo(
		() => ({
			placementId,
			cloudId: postOfficeContext?.tenantId,
			workspaceAri: isNullifiedSubscriptionVariableGateEnabled
				? (postOfficeContext?.workspaceAri ?? null)
				: postOfficeContext?.workspaceAri,
			product: isNullifiedSubscriptionVariableGateEnabled
				? (postOfficeContext?.product ?? null)
				: postOfficeContext?.product,
			activeScopes: isNullifiedSubscriptionVariableGateEnabled
				? (config.variables?.activeScopes ?? null)
				: config.variables?.activeScopes,
			...config.variables,
		}),
		[
			placementId,
			postOfficeContext?.tenantId,
			postOfficeContext?.product,
			postOfficeContext?.workspaceAri,
			isNullifiedSubscriptionVariableGateEnabled,
			config.variables,
		],
	);

	const adapterConfig = useMemo<AdapterSubscriptionConfig>(
		() => ({
			subscription: config.subscription,
			variables: subscriptionVariables,
		}),
		[config.subscription, subscriptionVariables],
	);

	const effectiveAdapter = useMemo(() => subscriptionAdapter ?? noOpAdapter, [subscriptionAdapter]);

	const adapterResult = effectiveAdapter.useSubscription(adapterConfig);

	return useMemo((): PlacementSubscriptionResult<T> => {
		if (!placementId) {
			return {
				data: null,
				error: new Error(
					'usePlacementSubscription must be used within a PlacementProvider with a valid placementId',
				),
				isLoading: false,
				isEnabledForLiveMessages: false,
			};
		}
		if (!isEnabledForLiveMessages) {
			return {
				data: null,
				error: null,
				isLoading: false,
				isEnabledForLiveMessages: false,
			};
		}
		return adapterResultToPlacementResult<T>(adapterResult as SubscriptionAdapterResult<T>, true);
	}, [placementId, isEnabledForLiveMessages, adapterResult]);
};
